import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { useState, useRef, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@Components/MuiWrappers/Autocomplete';

const QuickFind = (props) => {
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [isFocused, setIsFocused] = useState(false);
    const [activeSearch, setActiveSearch] = useState('Equipment');
    const searchTabs = props.searchTypes;
    const [backdropMargin, setBackdropMargin] = useState(0);
    const [backdropVisible, setBackdropVisible] = useState(false);
    const resizeObserverRef = useRef(null);
    const scrollPosition = useRef(0);
    const isClient = useRef(typeof window !== 'undefined');
    const quickFindWrapperRef = useRef(null);
    const isMobile = useRef(false);

    // Function to prevent scroll on backdrop
    const preventScroll = (e) => {
        e.preventDefault();
    };
    useEffect(() => {
        if (!isClient.current) return;

        // Detect mobile devices
        isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);     
    }, []);

    useEffect(() => {
        if (!isClient.current) return;

        if (navigator && "virtualKeyboard" in navigator) {
            navigator.virtualKeyboard.overlaysContent = true;
        }

        // Initialize measurements and observers
        setupResizeObserver();

        if (isFocused) {
            // Calculate backdrop margin
            const margin = getBackdropMargin();
            setBackdropMargin(margin);

            // Store current scroll position
            scrollPosition.current = window.pageYOffset;

            // For all devices, just prevent background scrolling without position:fixed
            document.body.style.overflow = 'hidden';

            // Set backdrop to visible after a small delay
            setTimeout(() => {
                setBackdropVisible(isFocused);
            }, 10);
        } else {
            setBackdropVisible(isFocused);
            // Reset styles
            document.body.style.overflow = '';
        }

        return () => {
            if (!isClient.current) return;

            // Disconnect the ResizeObserver
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }

            // Always reset styles on unmount
            document.body.style.overflow = '';
        };
    }, [isFocused]);

    const setupResizeObserver = () => {
        const container = document.querySelector(props.container);

        // Create a new ResizeObserver
        resizeObserverRef.current = new ResizeObserver(entries => {
            // Update the backdrop margin when observed elements resize
            const margin = getBackdropMargin();
            setBackdropMargin(margin);
        });

        if (container) {
            resizeObserverRef.current.observe(container);
        }
    };

    const setFocus = (e, key) => {
        e.preventDefault();
        setActiveSearch(key);
        setIsFocused(true);
    };

    const handleBlur = (e) => {
        if (e.relatedTarget === null) {
            setIsFocused(false);
        }
    };

    const getBackdropMargin = () => {
        const container = document.querySelector(props.container);
        if (container) {
            const rect = container.getBoundingClientRect();
            return rect.bottom;
        }
    };

    const closeBackdrop = () => {
        setBackdropVisible(false);
        // Wait for transition to complete before removing element
        setTimeout(() => {
            setIsFocused(false);
        }, 1000);
    };

    return (
        <div
            id={props.id}
            className='quick-find quick-find-wrapper'
            ref={quickFindWrapperRef}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            style={{
                position: 'relative',  // Makes component behave more predictably
                zIndex: isFocused ? 100001 : 'auto' // Ensure visibility when focused
            }}
        >
            {isFocused && (
                <div
                    className="quick-find-backdrop"
                    onClick={closeBackdrop}
                    onTouchMove={preventScroll}
                    style={{
                        position: 'fixed',
                        top: `${backdropMargin}px`,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 99999,
                        opacity: backdropVisible ? 1 : 0,
                        transition: 'opacity 0.5s ease',
                        pointerEvents: backdropVisible ? 'auto' : 'none',
                        touchAction: 'none',
                        willChange: 'opacity'
                    }}
                />
            )}

            {searchTabs && Object.keys(searchTabs).length > 1 &&
                <Collapse
                    in={isFocused}
                    timeout={500}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                >
                    <div className='search-selector'>
                        <ul>
                            {Object.entries(searchTabs).map(([key, value], index) => {
                                return (
                                    <li key={`search-tab-${index}`} onClick={(e) => setFocus(e, key)}>
                                        <button className={'search-type-button' + (activeSearch === key ? ' active' : '')}>
                                            {value}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </Collapse>
            }

            <Autocomplete
                name='QuickFindCode'
                label={props.placeholder}
                styles={{ bgVariant: currentSite?.Site?.IsRVUniverse ? 'white' : null }}
                textFieldStyles={{
                    backgroundColor: 'transparent',
                    willChange: 'transform',
                    transform: 'translateZ(0)'
                }}
                showHistory
                openOnFocus
                searchType={activeSearch}
                partsUrl={props.partsUrl}
                attachmentsUrl={props.attachmentsUrl}
                value={props.value}
                disableUnderline={true}
                hideLabel={props.hideLabel}
                onTextChange={props.onTextChange}
                isStickyHeader={props.isStickyHeader}
                isFocused={isFocused}
            />
        </div>
    );
};

export default QuickFind;